import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getItemKey } from "@/stores/DataTableStore";
import styles from "@/styles/DataTable.module.less";
import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Grid, List, Pagination, Space, Table, Typography, } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
const { useBreakpoint } = Grid;
const { Title } = Typography;
// Основной компонент DataTable, работающий с DataTableStore
export const DataTable = observer(({ store, config }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { columns, rowKey, header, scroll, size = "middle", className, cardClassName, tableClassName, onRow, selectionType, locale, } = config;
    // Получаем все данные из стора
    const { data: dataSource, loading, error, currentPage, pageSize, total, handleTableChange, initializeData, refresh, } = store;
    // Загружаем данные при монтировании компонента и очищаем при размонтировании
    useEffect(() => {
        initializeData();
    }, []);
    /** в идеале это нужно делать в конструкторе DataTableStore */
    useEffect(() => {
        store.setColumns(columns);
        store.setRowKey(rowKey);
    }, [columns, rowKey, store]);
    // Функция для рендера заголовка
    const renderHeader = () => {
        // Настройки для кнопки обновления
        const showMainHeader = !!header?.title || !!header?.actions || !!config.showRefreshButton;
        const showHeader = showMainHeader || !!header?.extraContent;
        if (!showHeader)
            return null;
        return (_jsxs(Flex, { vertical: true, gap: 16, children: [showMainHeader && (_jsxs("div", { className: styles.header, children: [_jsx(Title, { level: 2, children: header?.title }), _jsxs(Space, { children: [config.showRefreshButton && (_jsx(Button, { icon: _jsx(ReloadOutlined, {}), onClick: refresh, loading: loading, title: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u0434\u0430\u043D\u043D\u044B\u0435", children: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })), header?.actions] })] })), header?.extraContent] }));
    };
    // Функция для рендера ошибок
    const renderError = () => {
        if (!error)
            return null;
        return (_jsx(Alert, { message: error, type: "error", showIcon: true, closable: true, className: styles.error }));
    };
    const paginationParams = {
        current: currentPage,
        pageSize: pageSize,
        total: total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: (page, pageSize) => {
            handleTableChange({ current: page, pageSize });
        },
    };
    const mobilePaginationParams = {
        ...paginationParams,
        simple: true,
        showSizeChanger: false,
        showQuickJumper: false,
    };
    const handleSingleSelect = (record, selected) => {
        const key = getItemKey(record, rowKey);
        if (selected) {
            store.clearSelection();
            store.select(key);
        }
    };
    const handleMultipleSelect = (record, selected) => {
        const key = getItemKey(record, rowKey);
        store.select(key);
    };
    const multiple = selectionType === "checkbox";
    const rowSelection = selectionType
        ? {
            type: selectionType,
            selectedRowKeys: store.selectedRowKeys.map(String),
            onSelect: multiple ? handleMultipleSelect : handleSingleSelect,
            // выбрать все можно только в режиме множественного выбора
            onSelectAll: multiple
                ? (selected, _selectedRows, changeRows) => {
                    store.selectAll(selected, _selectedRows, changeRows);
                }
                : undefined,
        }
        : undefined;
    // Обработчик клика на строку для выбора (если включён selectionType)
    const handleRowClick = (record) => {
        if (!selectionType)
            return;
        const key = getItemKey(record, rowKey);
        const isSelected = store.selectedRowKeys.includes(key);
        if (multiple) {
            // Для checkbox - переключаем выбор
            store.select(key);
        }
        else {
            // Для radio - выбираем только если не выбрано
            if (!isSelected) {
                store.clearSelection();
                store.select(key);
            }
        }
    };
    // Объединяем пользовательский onRow с обработчиком выбора строки
    const mergedOnRow = (record, index) => {
        const userOnRow = onRow?.(record, index);
        return {
            ...userOnRow,
            onClick: (e) => {
                // Вызываем выбор строки при клике
                handleRowClick(record);
                // Вызываем пользовательский обработчик, если он есть
                userOnRow?.onClick?.(e);
            },
            style: {
                ...userOnRow?.style,
                cursor: selectionType ? "pointer" : userOnRow?.style?.cursor,
            },
        };
    };
    // Рендер карточек для мобильных устройств
    const renderMobileCards = () => (_jsxs(_Fragment, { children: [_jsx(List, { loading: loading, dataSource: dataSource, locale: locale?.emptyText && typeof locale.emptyText !== "function"
                    ? { emptyText: locale.emptyText }
                    : undefined, renderItem: (item) => {
                    const key = getItemKey(item, rowKey);
                    const isSelected = store.selectedRowKeys.includes(key);
                    return (_jsx(Card, { className: `${styles.mobileCard} ${isSelected ? styles.selected : ""}`, onClick: () => {
                            if (selectionType) {
                                multiple
                                    ? store.select(key)
                                    : (store.clearSelection(), store.select(key));
                            }
                            onRow?.(item, 0)?.onClick?.({});
                        }, children: columns
                            .filter((col) => col.dataIndex || col.render)
                            .map((col, i) => {
                            const value = item[col.dataIndex];
                            return (_jsxs("div", { className: styles.cardRow, children: [_jsxs("span", { className: styles.cardLabel, children: [String(col.title), ":"] }), _jsx("span", { className: styles.cardValue, children: col.render
                                            ? col.render(value, item, i)
                                            : String(value || "") })] }, String(col.key)));
                        }) }));
                } }), _jsx(Pagination, { ...mobilePaginationParams })] }));
    return (_jsxs("div", { className: `${styles.container} ${className || ""}`, children: [renderHeader(), renderError(), isMobile ? (_jsx("div", { className: styles.mobileContainer, children: renderMobileCards() })) : (_jsxs(Card, { className: `${styles.tableCard} ${cardClassName || ""}`, children: [_jsx(Table, { columns: columns, dataSource: dataSource, rowKey: rowKey, loading: loading, onChange: handleTableChange, scroll: scroll, size: size, className: `${styles.table} ${tableClassName || ""}`, rowSelection: rowSelection, onRow: mergedOnRow, pagination: false, locale: locale }), _jsx(Pagination, { ...paginationParams })] }))] }));
});
